// ** React Imports
import { createContext, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// ** Config
import {
  TestSeedListBase,
  TestSeedListById,
  TestSeedListMembers,
  TestSeedListMemberById,
  TestSeedListBulkDelete,
  TestSeedListBulkStatus
} from 'src/configs/api'
import { countries } from 'countries-list'
import { useAPI } from 'src/utils/api'
import { useToast } from 'src/@core/components/custom-Toast/customToast'
import { containsUnsafeMarkup } from 'src/utils/sanitizeHtml'

// Channels defined locally
export const TEST_SEED_LIST_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'rcs', label: 'RCS' }
]

// List types supported by the Test & Seed backend.
export const TEST_SEED_LIST_TYPES = [
  { value: 'test', label: 'Test' },
  { value: 'seed', label: 'Seed' },
  { value: 'test_seed', label: 'Test & Seed' }
]

const normalizeCsvValue = value => String(value ?? '').replace(/^\uFEFF/, '').trim()

// Loose shape checks for spotting data where a header should be. They are
// not validation: the backend decides what a valid member is.
const isEmailLike = value => value.includes('@') && value.includes('.')

const isPhoneLike = value => {
  const digits = (value.match(/\d/g) || []).length

  return digits >= 7 && digits === value.replace(/[\s\-+()]/g, '').length
}

// Generated column names for a paste with no header row, wide enough for
// the widest row so a short first row can't hide a column.
const placeholderHeaders = rows =>
  Array.from({ length: Math.max(0, ...rows.map(row => row.length)) }, (_, index) => `Column ${index + 1}`)

// With `allowHeaderless` (pasted text only), a first row holding an address
// or a phone number is data, not a header: every row is kept as data and the
// headers are generated. A file still needs its header row.
const parseCsvRows = (csvText, { allowHeaderless = false } = {}) => {
  const source = String(csvText ?? '').trim()
  if (!source) {
    return { headers: [], rows: [], headerless: false }
  }

  const rows = []
  let currentValue = ''
  let currentRow = []
  let inQuotes = false

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]
    const nextCharacter = source[index + 1]

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentValue += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (character === ',' && !inQuotes) {
      currentRow.push(currentValue)
      currentValue = ''
      continue
    }

    if ((character === '\n' || character === '\r') && !inQuotes) {
      if (character === '\r' && nextCharacter === '\n') {
        index += 1
      }
      currentRow.push(currentValue)
      if (currentRow.some(value => normalizeCsvValue(value) !== '')) {
        rows.push(currentRow)
      }
      currentRow = []
      currentValue = ''
      continue
    }

    currentValue += character
  }

  if (currentValue || currentRow.length) {
    currentRow.push(currentValue)
    if (currentRow.some(value => normalizeCsvValue(value) !== '')) {
      rows.push(currentRow)
    }
  }

  const normalizedRows = rows.map(row => row.map(value => normalizeCsvValue(value)))

  if (allowHeaderless && normalizedRows[0]?.some(value => isEmailLike(value) || isPhoneLike(value))) {
    return { headers: placeholderHeaders(normalizedRows), rows: normalizedRows, headerless: true }
  }

  const [headers = [], ...dataRows] = normalizedRows

  return {
    headers,
    rows: dataRows,
    headerless: false
  }
}

// Pre-fills Match for a paste with no header row, where there are no column
// names to go on: a column whose filled cells are mostly addresses suggests
// Email, mostly numbers suggests Phone. The user can change either on Match.
export const suggestMappingFromValues = (headers, rows) => {
  const counts = headers.map((_, index) => {
    const filled = rows.map(row => row[index] ?? '').filter(Boolean)

    return {
      filled: filled.length,
      emails: filled.filter(isEmailLike).length,
      phones: filled.filter(value => !isEmailLike(value) && isPhoneLike(value)).length
    }
  })

  // The column with the most matches wins, provided they are a majority of
  // its filled cells.
  const pick = (key, taken) => {
    let best = -1
    counts.forEach((count, index) => {
      if (index === taken || !count[key] || count[key] * 2 <= count.filled) {
        return
      }
      if (best < 0 || count[key] > counts[best][key]) {
        best = index
      }
    })

    return best
  }

  const emailIndex = pick('emails', -1)
  const phoneIndex = pick('phones', emailIndex)
  const mapping = {}
  if (emailIndex >= 0) {
    mapping.emailColumn = headers[emailIndex]
  }
  if (phoneIndex >= 0) {
    mapping.phoneColumn = headers[phoneIndex]
  }

  return mapping
}


const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_VALUE_REGEX = /^\+?[0-9()\-.\s]+$/
const MAX_MEMBER_FIELD_LENGTH = 255
const MIN_MEMBER_PHONE_DIGITS = 7
const MAX_MEMBER_PHONE_DIGITS = 15
const TEST_SEED_DEFAULT_COUNTRY = 'IN'

export const testSeedCountryCodeOptions = Object.entries(countries)
  .filter(([, data]) => data.phone?.length)
  .map(([isoCode, data]) => ({ isoCode, name: data.name, dialCode: `+${data.phone[0]}` }))
  .sort((a, b) => Number(a.dialCode) - Number(b.dialCode))

export const testSeedDialCodeFor = isoCode => testSeedCountryCodeOptions.find(option => option.isoCode === isoCode)?.dialCode ?? ''

const pendingDigitsOnly = value => String(value ?? '').replace(/\D/g, '')

export const parseTestSeedPhoneInput = (raw, isoCode) => {
  const digits = pendingDigitsOnly(raw)
  if (!String(raw ?? '').trim().startsWith('+')) {
    return { isoCode, phoneNumber: digits }
  }
  const currentCode = pendingDigitsOnly(testSeedDialCodeFor(isoCode))
  if (currentCode && digits.startsWith(currentCode)) {
    return { isoCode, phoneNumber: digits.slice(currentCode.length) }
  }
  const match = testSeedCountryCodeOptions
    .filter(option => digits.startsWith(pendingDigitsOnly(option.dialCode)))
    .sort((a, b) => b.dialCode.length - a.dialCode.length)[0]
  return match
    ? { isoCode: match.isoCode, phoneNumber: digits.slice(pendingDigitsOnly(match.dialCode).length) }
    : { isoCode, phoneNumber: digits }
}

let nextPendingMemberKey = 0
export const createPendingTestSeedMember = () => ({
  key: `test-seed-pending-${++nextPendingMemberKey}`,
  email: '',
  isoCode: TEST_SEED_DEFAULT_COUNTRY,
  phoneNumber: '',
  channelIdentifier: '',
  touched: {},
  serverErrors: undefined
})

const normalizedEmail = value => String(value ?? '').trim().toLowerCase()
const normalizedPhone = (value, isoCode) => `${testSeedDialCodeFor(isoCode)}${pendingDigitsOnly(value)}`
const isEmptyPendingMember = member =>
  !String(member.email ?? '').trim() && !String(member.phoneNumber ?? '').trim() && !String(member.channelIdentifier ?? '').trim()

export const toTestSeedMemberPayload = member => {
  const payload = {}
  const email = String(member.email ?? '').trim()
  const phoneNumber = member.phoneNumber ? normalizedPhone(member.phoneNumber, member.isoCode) : ''
  const channelIdentifier = String(member.channelIdentifier ?? '').trim()
  if (email) { payload.email = email }
  if (phoneNumber) { payload.phoneNumber = phoneNumber }
  if (channelIdentifier) { payload.channelIdentifier = channelIdentifier }
  return payload
}

export const validateTestSeedPendingMembers = pendingMembers => {
  const kept = pendingMembers.filter(member => !isEmptyPendingMember(member))
  const errors = {}
  const seen = new Map()
  const setError = (member, field, message) => {
    errors[member.key] = { ...errors[member.key], [field]: errors[member.key]?.[field] || message }
  }
  const addDuplicateKey = (member, field, value) => {
    if (!value) { return }
    const previous = seen.get(value)
    if (previous) {
      setError(member, field, 'Duplicate member')
      setError(previous.member, previous.field, 'Duplicate member')
    } else {
      seen.set(value, { member, field })
    }
  }

  kept.forEach(member => {
    const email = String(member.email ?? '').trim()
    const phoneNumber = String(member.phoneNumber ?? '').trim()
    const channelIdentifier = String(member.channelIdentifier ?? '').trim()
    if (!email && !phoneNumber && !channelIdentifier) {
      setError(member, 'email', 'Enter an email, phone or channel identifier')
      return
    }
    if (email) {
      if (email.length > MAX_MEMBER_FIELD_LENGTH) {
        setError(member, 'email', `Must be ${MAX_MEMBER_FIELD_LENGTH} characters or fewer`)
      } else if (!EMAIL_REGEX.test(email)) {
        setError(member, 'email', 'Invalid email format')
      } else {
        addDuplicateKey(member, 'email', `email:${normalizedEmail(email)}`)
      }
    }
    if (phoneNumber) {
      const digits = pendingDigitsOnly(phoneNumber)
      if (!PHONE_VALUE_REGEX.test(phoneNumber)) {
        setError(member, 'phoneNumber', 'Enter a valid phone number')
      } else if (digits.startsWith('0')) {
        setError(member, 'phoneNumber', 'Leave out the leading 0')
      } else if (digits.length < MIN_MEMBER_PHONE_DIGITS) {
        setError(member, 'phoneNumber', `Enter at least ${MIN_MEMBER_PHONE_DIGITS} digits`)
      } else if (digits.length + pendingDigitsOnly(testSeedDialCodeFor(member.isoCode)).length > MAX_MEMBER_PHONE_DIGITS) {
        setError(member, 'phoneNumber', 'Too many digits for a phone number')
      } else {
        addDuplicateKey(member, 'phoneNumber', `phone:${normalizedPhone(phoneNumber, member.isoCode)}`)
      }
    }
    if (channelIdentifier) {
      if (channelIdentifier.length > MAX_MEMBER_FIELD_LENGTH) {
        setError(member, 'channelIdentifier', `Must be ${MAX_MEMBER_FIELD_LENGTH} characters or fewer`)
      } else if (channelIdentifier.includes('@') && !EMAIL_REGEX.test(channelIdentifier)) {
        setError(member, 'channelIdentifier', 'Invalid email format')
      } else if (!channelIdentifier.includes('@') && PHONE_VALUE_REGEX.test(channelIdentifier) && /\d/.test(channelIdentifier)) {
        const digits = pendingDigitsOnly(channelIdentifier)
        if (digits.length >= MIN_MEMBER_PHONE_DIGITS && digits.length > MAX_MEMBER_PHONE_DIGITS) {
          setError(member, 'channelIdentifier', 'Enter a valid phone number or channel identifier')
        }
      }
      if (!errors[member.key]?.channelIdentifier) {
        addDuplicateKey(member, 'channelIdentifier', `identifier:${channelIdentifier.toLowerCase()}`)
      }
    }
  })

  return {
    checked: kept.map(member => ({ ...member, touched: { email: true, phoneNumber: true, channelIdentifier: true } })),
    toSend: kept.filter(member => !errors[member.key]),
    errors,
    anyInvalid: Object.keys(errors).length > 0
  }
}

const isValidEmailValue = value => {
  const normalized = String(value ?? '').trim()

  return Boolean(normalized) && EMAIL_REGEX.test(normalized)
}

const isValidPhoneValue = value => {
  const normalized = String(value ?? '').trim()

  if (!normalized || !PHONE_VALUE_REGEX.test(normalized)) {
    return false
  }

  const digitsOnly = normalized.replace(/\D/g, '')

  return digitsOnly.length >= 7 && digitsOnly.length <= 15
}

const isValidImportMember = member => {
  if (!member || !(member.email || member.phoneNumber || member.channelIdentifier)) {
    return false
  }

  if (member.email && !isValidEmailValue(member.email)) {
    return false
  }

  if (member.phoneNumber && !isValidPhoneValue(member.phoneNumber)) {
    return false
  }

  if (member.channelIdentifier) {
    const channelIdentifier = String(member.channelIdentifier).trim()

    if (!channelIdentifier) {
      return false
    }

    if (channelIdentifier.includes('@')) {
      return isValidEmailValue(channelIdentifier)
    }

    if (/\d/.test(channelIdentifier)) {
      return isValidPhoneValue(channelIdentifier)
    }
  }

  return true
}

const buildRowMemberFromMapping = (row, headers, userMapping) => {
  const member = {}

  userMapping.forEach(mapping => {
    if (!mapping?.included || mapping.mapsTo === 'IGNORE') {
      return
    }

    const columnIndex = headers.indexOf(mapping.header)
    if (columnIndex === -1) {
      return
    }

    const rawValue = normalizeCsvValue(row[columnIndex] ?? '')
    if (!rawValue) {
      return
    }

    if (mapping.mapsTo === 'EMAIL') {
      member.email = rawValue
    } else if (mapping.mapsTo === 'PHONE') {
      member.phoneNumber = rawValue
    } else if (mapping.mapsTo === 'CHANNEL_IDENTIFIER') {
      member.channelIdentifier = rawValue
      if (rawValue.includes('@')) {
        member.email = member.email || rawValue
      } else if (/\d/.test(rawValue)) {
        member.phoneNumber = member.phoneNumber || rawValue
      }
    }
  })

  return member
}

export const buildImportMemberPayload = (csvText, userMapping, _selectedChannels = [], csvOptions = {}) => {
  const { headers, rows: parsedRows } = parseCsvRows(csvText, csvOptions)

  if (!headers.length || !parsedRows.length || !Array.isArray(userMapping) || !userMapping.length) {
    return []
  }

  return parsedRows.reduce((importMembers, row) => {
    const member = buildRowMemberFromMapping(row, headers, userMapping)

    if (isValidImportMember(member)) {
      importMembers.push(member)
    }

    return importMembers
  }, [])
}

export const buildImportSummary = (csvText, userMapping, csvOptions = {}) => {
  const { headers, rows: parsedRows } = parseCsvRows(csvText, csvOptions)

  if (!headers.length || !parsedRows.length || !Array.isArray(userMapping) || !userMapping.length) {
    return { rowsToApply: 0, skippedEmptyRows: 0, skippedInvalidRows: 0 }
  }

  let rowsToApply = 0
  let skippedEmptyRows = 0
  let skippedInvalidRows = 0

  parsedRows.forEach(row => {
    const member = buildRowMemberFromMapping(row, headers, userMapping)
    const hasIdentifier = Boolean(member.email || member.phoneNumber || member.channelIdentifier)

    if (!hasIdentifier) {
      skippedEmptyRows += 1
      return
    }

    if (isValidImportMember(member)) {
      rowsToApply += 1
      return
    }

    skippedInvalidRows += 1
  })

  return { rowsToApply, skippedEmptyRows, skippedInvalidRows }
}

export const validateTestSeedMemberChannels = (members, channels) => {
  const selectedChannels = new Set((channels || []).map(channel => String(channel ?? '').trim().toLowerCase()))
  const hasEmail = (members || []).some(member => member?.email || String(member?.channelIdentifier || '').includes('@'))
  const hasPhone = (members || []).some(member => {
    const identifier = member?.phoneNumber || member?.channelIdentifier || ''
    return Boolean(member?.phoneNumber || (!String(identifier).includes('@') && /\d/.test(identifier)))
  })

  if (hasEmail && !selectedChannels.has('email')) {
    return 'Email channel must be selected for members containing email addresses.'
  }
  if (hasPhone && !['sms', 'whatsapp', 'rcs'].some(channel => selectedChannels.has(channel))) {
    return 'SMS, WhatsApp, or RCS must be selected for members containing phone numbers.'
  }
  return null
}

export const validateTestSeedListDescription = description => {
  const normalizedDescription = typeof description === 'string' ? description : String(description ?? '')

  if (normalizedDescription.length > 1000) {
    throw new Error('Description must be 1000 characters or fewer')
  }

  return normalizedDescription
}

export const getTestSeedListNameError = name => {
  const normalizedName = String(name ?? '').trim()

  if (!normalizedName) {
    return 'List name is required.'
  }

  if (normalizedName.length > 255) {
    return 'Name must be 255 characters or fewer'
  }

  if (containsUnsafeMarkup(normalizedName)) {
    return 'List name cannot contain script or event-handler markup.'
  }

  return ''
}

export const buildFinalImportPayload = ({ name, type, channels, members = [], description = '', action = null }) => {
  const normalizedMembers = Array.isArray(members)
    ? members
        .map(member => {
          const channelIdentifier = String(member?.channelIdentifier ?? '').trim()
          const email = member?.email ? String(member.email).trim() : ''
          const phoneNumber = member?.phoneNumber ? String(member.phoneNumber).trim() : ''

          const payloadMember = {}
          if (channelIdentifier) {
            payloadMember.channelIdentifier = channelIdentifier
          }
          if (email) {
            payloadMember.email = email
          }
          if (phoneNumber) {
            payloadMember.phoneNumber = phoneNumber
          }

          return payloadMember
        })
        .filter(member => member.channelIdentifier || member.email || member.phoneNumber)
    : []

  if (action) {
    const normalizedAction = String(action).trim().toLowerCase()
    if (!['update', 'delete'].includes(normalizedAction)) {
      throw new Error('Import action must be either update or delete.')
    }

    return {
      action: normalizedAction,
      members: normalizedMembers
    }
  }

  const cleanName = String(name ?? '').trim()
  const nameError = getTestSeedListNameError(cleanName)
  if (nameError) {
    throw new Error(nameError)
  }

  const normalizedType = String(type ?? '').trim().toLowerCase()
  if (!['test', 'seed', 'test_seed'].includes(normalizedType)) {
    throw new Error('List type must be one of: test, seed, test_seed.')
  }

  const normalizedChannels = Array.isArray(channels)
    ? [...new Set(channels.map(channel => String(channel ?? '').trim().toLowerCase()).filter(Boolean))]
    : []

  if (!normalizedChannels.length) {
    throw new Error('At least one channel is required.')
  }

  const normalizedDescription = validateTestSeedListDescription(description)

  return {
    name: cleanName,
    type: normalizedType,
    channels: normalizedChannels,
    members: normalizedMembers,
    description: normalizedDescription
  }
}

export const normalizeTestSeedListItem = item => {
  const internalId = item?.id ?? item?.ID ?? null
  const explicitUuid = item?.uuid ?? item?.UUID ?? null
  const inferredUuid =
    !explicitUuid && typeof internalId === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(internalId)
      ? internalId
      : null

  return {
    ...item,
    id: internalId ?? explicitUuid,
    uuid: explicitUuid ?? inferredUuid,
    description: item?.description ?? ''
  }
}

const defaultProvider = {
  rows: [],
  setRows: () => null,
  rowCount: 0,
  setRowCount: () => null,
  selectedList: null,
  setSelectedList: () => null,
  fetchTestSeedLists: () => Promise.resolve(),
  createTestSeedList: () => Promise.resolve(null),
  getTestSeedListById: () => Promise.resolve(null),
  updateTestSeedList: () => Promise.resolve(null),
  deleteTestSeedList: () => Promise.resolve(false),
  bulkDeleteTestSeedLists: () => Promise.resolve({ totalRequested: 0, deletedCount: 0, deletedIds: [], skippedIds: [] }),
  bulkArchiveTestSeedLists: () => Promise.resolve({ totalRequested: 0, updatedCount: 0, updatedIds: [], skippedIds: [] }),
  bulkUnarchiveTestSeedLists: () => Promise.resolve({ totalRequested: 0, updatedCount: 0, updatedIds: [], skippedIds: [] }),
  archiveTestSeedList: () => Promise.resolve(null),
  unarchiveTestSeedList: () => Promise.resolve(null),

  members: [],
  setMembers: () => null,
  memberTotal: 0,
  setMemberTotal: () => null,
  getMembers: () => Promise.resolve(),
  updateMember: () => Promise.resolve(false),
  deleteMember: () => Promise.resolve(false),
  pendingMembers: [],
  addPendingMember: () => null,
  updatePendingMember: () => null,
  removePendingMember: () => null,
  setPendingMembers: () => null,
  validatePendingMembers: () => ({ checked: [], toSend: [], errors: {}, anyInvalid: false }),
  addMembers: () => Promise.resolve({ ok: false, results: [], addedCount: 0 }),

  wizardStep: 0,
  setWizardStep: () => null,
  detailsName: '',
  setDetailsName: () => null,
  detailsType: 'test_seed',
  setDetailsType: () => null,
  detailsChannels: [],
  setDetailsChannels: () => null,
  detailsDescription: '',
  setDetailsDescription: () => null,
  detailsDescriptionError: '',
  setDetailsDescriptionError: () => null,
  detailsError: '',
  setDetailsError: () => null,
  wizardAction: 'update',
  setWizardAction: () => null,

  importMethod: 'file',
  setImportMethod: () => null,
  files: [],
  setFiles: () => null,
  csvData: '',
  setCsvData: () => null,
  importHeaderless: false,
  wizardListId: '',
  setWizardListId: () => null,
  draftId: '',
  setDraftId: () => null,
  detectedHeaders: [],
  setDetectedHeaders: () => null,
  generatedMapping: {},
  setGeneratedMapping: () => null,
  userMapping: [],
  setUserMapping: () => null,
  previewSummary: null,
  setPreviewSummary: () => null,
  commitStatus: 'idle',
  setCommitStatus: () => null,
  importDraftStatus: null,
  setImportDraftStatus: () => null,
  detectImport: () => Promise.resolve(null),
  previewImport: () => Promise.resolve(null),
  commitImport: () => Promise.resolve(null),
  pollImportStatus: () => Promise.resolve(null),
  resetImportWizard: () => null,
  resetListForm: () => null
}

const TestSeedListContext = createContext(defaultProvider)

const TestSeedListProvider = ({ children }) => {
  const [rows, setRows] = useState(defaultProvider.rows)
  const [rowCount, setRowCount] = useState(defaultProvider.rowCount)
  const [selectedList, setSelectedList] = useState(defaultProvider.selectedList)

  const [members, setMembers] = useState(defaultProvider.members)
  const [memberTotal, setMemberTotal] = useState(defaultProvider.memberTotal)
  const [pendingMembers, setPendingMembers] = useState(defaultProvider.pendingMembers)

  const [wizardStep, setWizardStep] = useState(defaultProvider.wizardStep)
  const [detailsName, setDetailsName] = useState(defaultProvider.detailsName)
  const [detailsType, setDetailsType] = useState(defaultProvider.detailsType)
  const [detailsChannels, setDetailsChannels] = useState(defaultProvider.detailsChannels)
  const [detailsDescription, setDetailsDescription] = useState(defaultProvider.detailsDescription)
  const [detailsDescriptionError, setDetailsDescriptionError] = useState(defaultProvider.detailsDescriptionError)
  const [detailsError, setDetailsError] = useState(defaultProvider.detailsError)
  const [wizardAction, setWizardAction] = useState(defaultProvider.wizardAction)

  const [importMethod, setImportMethod] = useState(defaultProvider.importMethod)
  const [files, setFiles] = useState(defaultProvider.files)
  const [csvData, setCsvData] = useState(defaultProvider.csvData)

  // Only pasted text may skip its header row.
  const csvOptions = useMemo(() => ({ allowHeaderless: importMethod === 'paste' }), [importMethod])
  const importHeaderless = useMemo(() => parseCsvRows(csvData, csvOptions).headerless, [csvData, csvOptions])
  const [wizardListId, setWizardListId] = useState(defaultProvider.wizardListId)
  const [draftId, setDraftId] = useState(defaultProvider.draftId)
  const [detectedHeaders, setDetectedHeaders] = useState(defaultProvider.detectedHeaders)
  const [generatedMapping, setGeneratedMapping] = useState(defaultProvider.generatedMapping)
  const [userMapping, setUserMapping] = useState(defaultProvider.userMapping)
  const [previewSummary, setPreviewSummary] = useState(defaultProvider.previewSummary)
  const [commitStatus, setCommitStatus] = useState(defaultProvider.commitStatus)
  const [importDraftStatus, setImportDraftStatus] = useState(defaultProvider.importDraftStatus)

  const { showSuccess, showError } = useToast()
  const api = useAPI()

 const fetchTestSeedLists = useCallback(async payload => {
  try {
    // Convert listing payload to query params expected by backend
    const page = payload?.pagination?.page ?? 1
    const pageSize = payload?.pagination?.pageSize ?? 25
    const sortField = payload?.sorting?.field
    const sortOrder = payload?.sorting?.order
    const searchKeyword = payload?.filter?.searchKeyword ?? ''

    const status = Array.isArray(payload?.filter?.status)
      ? payload.filter.status
      : []

    const type = Array.isArray(payload?.filter?.type)
      ? payload.filter.type
      : []

    const channels = Array.isArray(payload?.filter?.channels)
      ? payload.filter.channels
      : []

    const params = {
      page,
      pageSize,
      searchKeyword
    }

    if (sortField) {
      params.sortField = sortField
    }

    if (sortOrder) {
      params.sortOrder = sortOrder
    }

    if (status.length) {
      params.status = status.join(',')
    }

    if (type.length) {
      params.types = type.join(',')
    }

    if (channels.length) {
      params.channels = channels.join(',')
    }

    const response = await api.get(TestSeedListBase, { params })
    const d = response?.data

    // Support several possible backend shapes:
    // - { testSeedLists: [...], total }
    // - { data: { testSeedLists: [...], total } }
    // - direct array payload: [...]
    // - legacy keys: lists, items, results
    let lists = []
    let total = 0

    if (Array.isArray(d)) {
      lists = d
    } else if (d?.testSeedLists) {
      lists = d.testSeedLists
      total = d.total ?? total
    } else if (d?.data?.testSeedLists) {
      lists = d.data.testSeedLists
      total = d.data.total ?? total
    } else if (d?.lists) {
      lists = d.lists
      total = d.total ?? total
    } else if (d?.items) {
      lists = d.items
      total = d.total ?? total
    } else if (d?.results) {
      lists = d.results
      total = d.total ?? total
    } else if (d) {
      // If API returned a single object that represents one list, wrap it.
      if (typeof d === 'object' && Object.keys(d).length > 0) {
        // Heuristic: if it looks like a TestSeedList object (has name/type), wrap
        if (d.name || d.type || d.channels) {
          lists = [d]
          total = 1
        }
      }
    }

    // Keep DataGrid identity (`id`) and public resource identifier (`uuid`) separate.
    const normalized = (lists || []).map(normalizeTestSeedListItem)

    setRows(normalized)
    setRowCount(total ?? 0)
  } catch {
    // handled by the global toast interceptor in useAPI
  }
}, [api])

  const createTestSeedList = useCallback(async payload => {
    try {
      setDetailsError('')
      setDetailsDescriptionError('')
      const description = payload?.description ?? ''
      if (String(description).length > 1000) {
        setDetailsDescriptionError('Description must be 1000 characters or fewer')
        return null
      }
      const response = await api.post(TestSeedListBase, payload)
      return response?.data ?? null
    } catch (error) {
      setDetailsError(error?.response?.data?.message || 'Could not create this list. Please try again.')
      return null
    }
  }, [api])

  const getTestSeedListById = useCallback(async id => {
    try {
      const response = await api.get(TestSeedListById.replace(':listId', id))
      const payload = response?.data ?? null
      // Support direct, wrapped, and nested API response shapes.
      const data = payload?.data ?? payload
      const list = data?.testSeedList ?? payload?.testSeedList ?? data
      const normalizedList = list ? normalizeTestSeedListItem(list) : null
      setSelectedList(normalizedList)
      setWizardAction('update')
      const membersFromPayload = data?.members ?? payload?.members ?? list?.members ?? []
      setMembers(membersFromPayload)
      setMemberTotal(data?.total ?? list?.memberCount ?? membersFromPayload.length)
      return { testSeedList: normalizedList, members: membersFromPayload }
    } catch {
      return null
    }
  }, [api])

  const updateTestSeedList = useCallback(async (id, payload, successMessage = 'Test & Seed list updated successfully!') => {
    try {
      setDetailsError('')
      setDetailsDescriptionError('')
      const description = payload?.description ?? ''
      if (String(description).length > 1000) {
        setDetailsDescriptionError('Description must be 1000 characters or fewer')
        return null
      }
      const response = await api.patch(TestSeedListById.replace(':listId', id), payload)
      showSuccess(successMessage)
      return response?.data ?? null
    } catch (error) {
      setDetailsError(error?.response?.data?.message || 'Could not update this list. Please try again.')
      return null
    }
  }, [api, showSuccess])

  const deleteTestSeedList = useCallback(async id => {
    try {
      await api.delete(TestSeedListById.replace(':listId', id))
      showSuccess('Test & Seed list deleted successfully!')
      return true
    } catch {
      return false
    }
  }, [api, showSuccess])

  const reportSkipped = useCallback((skippedIds = [], blockedByCampaigns = {}, failedVerb) => {
    const blocked = blockedByCampaigns || {}
    const blockedIds = (skippedIds || []).filter(id => (blocked[id] || []).length > 0)
    const otherCount = (skippedIds || []).length - blockedIds.length

    if (blockedIds.length > 0) {
      const campaigns = [...new Set(blockedIds.flatMap(id => blocked[id] || []))]
      showError(
        `${blockedIds.length} Test & Seed list${blockedIds.length === 1 ? '' : 's'} could not be ${failedVerb} because ${
          blockedIds.length === 1 ? 'it is' : 'they are'
        } in use by: ${campaigns.join(', ')}.`
      )
    }

    if (otherCount > 0) {
      showError(`${otherCount} Test & Seed list${otherCount === 1 ? '' : 's'} could not be ${failedVerb}.`)
    }
  }, [showError])

  const bulkDeleteTestSeedLists = useCallback(async ids => {
    try {
      const response = await api.delete(TestSeedListBulkDelete, { data: { listIds: ids } })
      const payload = response?.data?.data ?? response?.data ?? {}
      const { deletedCount = 0, deletedIds = [], skippedIds = [], blockedByCampaigns } = payload

      if (deletedCount > 0) {
        showSuccess(`${deletedCount} Test & Seed list${deletedCount === 1 ? '' : 's'} deleted successfully!`)
      }

      return payload ?? { totalRequested: ids.length, deletedCount: 0, deletedIds: [], skippedIds: ids }
    } catch {
      return { totalRequested: ids.length, deletedCount: 0, deletedIds: [], skippedIds: ids, blockedByCampaigns: {} }
    }
  }, [api, showSuccess])

  const bulkUpdateTestSeedListsStatus = useCallback(async (ids, status) => {
    const verb = status === 'archived' ? 'archived' : 'moved back to active'
    try {
      const response = await api.put(TestSeedListBulkStatus, { listIds: ids, status })
      const { updatedCount = 0, skippedIds = [], blockedByCampaigns } = response?.data ?? {}

      if (updatedCount > 0) {
        showSuccess(`${updatedCount} Test & Seed list${updatedCount === 1 ? '' : 's'} ${verb}!`)
      }
      reportSkipped(skippedIds, blockedByCampaigns, 'updated')
      return response?.data ?? { totalRequested: ids.length, updatedCount: 0, updatedIds: [], skippedIds: ids }
    } catch {
      return { totalRequested: ids.length, updatedCount: 0, updatedIds: [], skippedIds: ids }
    }
  }, [api, reportSkipped, showSuccess])

  const bulkArchiveTestSeedLists = useCallback(ids => bulkUpdateTestSeedListsStatus(ids, 'archived'), [bulkUpdateTestSeedListsStatus])
  const bulkUnarchiveTestSeedLists = useCallback(ids => bulkUpdateTestSeedListsStatus(ids, 'active'), [bulkUpdateTestSeedListsStatus])

  const archiveTestSeedList = useCallback(row =>
    updateTestSeedList(
      row.id,
      {
        name: row.name,
        type: row.type,
        channels: row.channels || [],
        description: row.description ?? '',
        status: 'archived'
      },
      'Test & Seed list archived successfully!'
    ),
  [updateTestSeedList])

  const unarchiveTestSeedList = useCallback(row =>
    updateTestSeedList(
      row.id,
      {
        name: row.name,
        type: row.type,
        channels: row.channels || [],
        description: row.description ?? '',
        status: 'active'
      },
      'Test & Seed list moved back to active!'
    ),
  [updateTestSeedList])

  const getMembers = useCallback(async (listId, { page = 1, pageSize = 25, searchKeyword = '' } = {}) => {
    try {
      const response = await api.get(TestSeedListMembers.replace(':listId', listId), {
        params: { page, pageSize, searchKeyword }
      })
      const payload = response?.data?.data ?? response?.data ?? {}
      setMembers(payload?.members ?? [])
      setMemberTotal(payload?.total ?? 0)
    } catch {
      // handled by the global toast interceptor in useAPI
    }
  }, [api])

  const updateMember = useCallback(async (listId, memberId, payload) => {
    try {
      const response = await api.patch(
        TestSeedListMemberById.replace(':listId', listId).replace(':memberId', memberId),
        payload
      )
      showSuccess('Test & Seed list member updated successfully!')
      return response?.data ?? null
    } catch {
      return null
    }
  }, [api, showSuccess])

  const deleteMember = useCallback(async (listId, memberId) => {
    try {
      await api.delete(TestSeedListMemberById.replace(':listId', listId).replace(':memberId', memberId))
      showSuccess('Test & Seed list member removed successfully!')
      return true
    } catch {
      return false
    }
  }, [api, showSuccess])

  const addPendingMember = useCallback(() => {
    setPendingMembers(previous => [...previous, createPendingTestSeedMember()])
  }, [])

  const updatePendingMember = useCallback((key, patch) => {
    setPendingMembers(previous => previous.map(member =>
      member.key === key ? { ...member, ...patch, serverErrors: undefined } : member
    ))
  }, [])

  const removePendingMember = useCallback(key => {
    setPendingMembers(previous => previous.filter(member => member.key !== key))
  }, [])

  const validatePendingMembers = useCallback(() => validateTestSeedPendingMembers(pendingMembers), [pendingMembers])

  const addMembers = useCallback(async (listId, membersToAdd) => {
    try {
      const response = await api.post(TestSeedListMembers.replace(':listId', listId), { members: membersToAdd }, {
        skipGlobalToast: true
      })
      const data = response?.data?.data ?? response?.data ?? {}
      return {
        ok: true,
        results: data?.results ?? [],
        addedCount: data?.addedCount ?? data?.added ?? 0,
        duplicateCount: data?.duplicateCount ?? data?.duplicate ?? 0
      }
    } catch (error) {
      const data = error?.response?.data?.data ?? error?.response?.data ?? {}
      return {
        ok: false,
        results: data?.results ?? [],
        addedCount: data?.addedCount ?? 0,
        duplicateCount: data?.duplicateCount ?? 0,
        message: data?.message || ''
      }
    }
  }, [api])

  const detectImport = useCallback(async payload => {
    try {
      const sourceText = payload?.pastedText || csvData || ''
      const { headers, rows: csvRows, headerless } = parseCsvRows(sourceText, csvOptions)
      if (!headers.length || !csvRows.length) {
        const message = csvOptions.allowHeaderless
          ? 'Please paste at least one row of data.'
          : 'Please upload a CSV file with a header row and at least one data row.'
        setDetectedHeaders([])
        setGeneratedMapping({})
        setImportDraftStatus({ status: 'failed', errorMessage: message })
        showError(message)
        return null
      }

      const identifierHeader = headers.find(header => /email|phone|mobile|number|identifier/i.test(header)) || headers[0]
      const suggestedMapping = headerless
        ? suggestMappingFromValues(headers, csvRows)
        : { channelIdentifierColumn: identifierHeader }
      const nextDraftId = payload?.listId || wizardListId || draftId || `test-seed-import-${Date.now()}`

      setDraftId(nextDraftId)
      setDetectedHeaders(headers)
      setGeneratedMapping(suggestedMapping)
      setImportDraftStatus({ status: 'preview-ready', draftId: nextDraftId, detectedHeaders: headers })

      return { draftId: nextDraftId, detectedHeaders: headers, suggestedMapping }
    } catch {
      showError('Something went wrong while reading the CSV file.')
      return null
    }
  }, [csvData, csvOptions, draftId, showError, wizardListId])

  const previewImport = useCallback(async (draft, mapping) => {
    const previewDraftId = draft || wizardListId || draftId
    const summary = buildImportSummary(csvData, mapping || userMapping, csvOptions)

    if (previewDraftId) {
      setDraftId(previewDraftId)
    }

    setPreviewSummary(summary)
    setImportDraftStatus({
      status: 'preview-ready',
      draftId: previewDraftId,
      importedRows: summary.rowsToApply,
      skippedRows: summary.skippedEmptyRows + summary.skippedInvalidRows
    })

    return summary
  }, [csvData, csvOptions, draftId, userMapping, wizardListId])

  const commitImport = useCallback(async draft => {
    const importDraftId = draft || wizardListId || draftId

    if (!importDraftId) {
      showError('Create the list before the final import.')
      setCommitStatus('failed')
      setImportDraftStatus({ status: 'failed', draftId: importDraftId || '', errorMessage: 'Create the list before the final import.' })
      return null
    }

    try {
      const importMembers = buildImportMemberPayload(csvData, userMapping, detailsChannels, csvOptions)
      const isEditImport = Boolean(selectedList)
      const finalPayload = isEditImport
        ? { action: wizardAction, members: importMembers }
        : buildFinalImportPayload({
          name: detailsName,
          type: detailsType,
          channels: detailsChannels,
          description: detailsDescription,
          members: importMembers
        })

      const channelValidationError = validateTestSeedMemberChannels(importMembers, detailsChannels)
      if (channelValidationError) {
        setCommitStatus('failed')
        setImportDraftStatus({ status: 'failed', draftId: importDraftId, errorMessage: channelValidationError })
        showError(channelValidationError)
        return null
      }

      const summary = buildImportSummary(csvData, userMapping, csvOptions)

      if (!importMembers.length) {
        setCommitStatus('failed')
        setImportDraftStatus({ status: 'failed', draftId: importDraftId, errorMessage: 'No valid recipients were found in the uploaded file.' })
        showError('No valid recipients were found in the uploaded file.')
        return null
      }

      setDraftId(importDraftId)
      setCommitStatus('processing')
      setImportDraftStatus({
        status: 'processing',
        draftId: importDraftId,
        importedRows: summary.rowsToApply,
        skippedRows: summary.skippedEmptyRows + summary.skippedInvalidRows
      })

      try {
        const requestUrl = isEditImport
          ? `${TestSeedListById.replace(':listId', importDraftId)}/import`
          : TestSeedListById.replace(':listId', importDraftId)

        const response = await (isEditImport ? api.post(requestUrl, finalPayload) : api.patch(requestUrl, finalPayload))
        return response?.data ?? null
      } catch {
        setCommitStatus('failed')
        setImportDraftStatus({ status: 'failed', draftId: importDraftId, errorMessage: 'The import could not be completed.' })
        return null
      }
    } catch (error) {
      const message = error?.message || 'The import could not be completed.'
      setDetailsError(message)
      setCommitStatus('failed')
      setImportDraftStatus({ status: 'failed', draftId: importDraftId, errorMessage: message })
      showError(message)
      return null
    }
  }, [api, csvData, csvOptions, detailsChannels, detailsDescription, detailsName, detailsType, draftId, selectedList, showError, userMapping, wizardAction, wizardListId])

  const pollImportStatus = useCallback(async draftIdToPoll => {
    const activeDraftId = draftIdToPoll || draftId || wizardListId

    // Attempt to fetch the list by id — a successful GET implies the
    // backend has persisted the updated list and import is complete.
    try {
      const response = await api.get(TestSeedListById.replace(':listId', activeDraftId), { skipGlobalToast: true })
      // Build a completion-like status for the Review step to consume.
      const summary = buildImportSummary(csvData, userMapping, csvOptions)
      const completed = {
        status: 'completed',
        draftId: activeDraftId,
        importedRows: summary.rowsToApply,
        skippedRows: summary.skippedEmptyRows + summary.skippedInvalidRows,
        data: response?.data ?? null
      }
      setImportDraftStatus(completed)
      setCommitStatus('completed')
      return completed
    } catch {
      // If GET fails, continue reporting processing (the Review poll will retry).
      const summary = buildImportSummary(csvData, userMapping, csvOptions)
      const processing = {
        status: importDraftStatus?.status || 'processing',
        draftId: activeDraftId,
        importedRows: summary.rowsToApply,
        skippedRows: summary.skippedEmptyRows + summary.skippedInvalidRows
      }
      setImportDraftStatus(processing)
      return processing
    }
  }, [api, csvData, csvOptions, draftId, userMapping, wizardListId, importDraftStatus])

  const resetImportWizard = useCallback(() => {
    setSelectedList(defaultProvider.selectedList)
    setWizardStep(defaultProvider.wizardStep)
    setDetailsName(defaultProvider.detailsName)
    setDetailsType(defaultProvider.detailsType)
    setDetailsChannels(defaultProvider.detailsChannels)
    setDetailsDescription(defaultProvider.detailsDescription)
    setDetailsDescriptionError(defaultProvider.detailsDescriptionError)
    setDetailsError(defaultProvider.detailsError)
    setWizardAction(defaultProvider.wizardAction)
    setImportMethod(defaultProvider.importMethod)
    setFiles(defaultProvider.files)
    setCsvData(defaultProvider.csvData)
    setWizardListId(defaultProvider.wizardListId)
    setDraftId(defaultProvider.draftId)
    setDetectedHeaders(defaultProvider.detectedHeaders)
    setGeneratedMapping(defaultProvider.generatedMapping)
    setUserMapping(defaultProvider.userMapping)
    setPreviewSummary(defaultProvider.previewSummary)
    setCommitStatus(defaultProvider.commitStatus)
    setImportDraftStatus(defaultProvider.importDraftStatus)
    setPendingMembers(defaultProvider.pendingMembers)
  }, [])

  const resetListForm = useCallback(() => {
    setSelectedList(defaultProvider.selectedList)
    setDetailsName(defaultProvider.detailsName)
    setDetailsType(defaultProvider.detailsType)
    setDetailsChannels(defaultProvider.detailsChannels)
    setDetailsDescription(defaultProvider.detailsDescription)
    setDetailsDescriptionError(defaultProvider.detailsDescriptionError)
    setDetailsError(defaultProvider.detailsError)
    setWizardAction(defaultProvider.wizardAction)
  }, [])

  const values = useMemo(
    () => ({
      rows,
      setRows,
      rowCount,
      setRowCount,
      selectedList,
      setSelectedList,
      fetchTestSeedLists,
      createTestSeedList,
      getTestSeedListById,
      updateTestSeedList,
      deleteTestSeedList,
      bulkDeleteTestSeedLists,
      bulkArchiveTestSeedLists,
      bulkUnarchiveTestSeedLists,
      archiveTestSeedList,
      unarchiveTestSeedList,

      members,
      setMembers,
      memberTotal,
      setMemberTotal,
      getMembers,
      updateMember,
      deleteMember,
      pendingMembers,
      setPendingMembers,
      addPendingMember,
      updatePendingMember,
      removePendingMember,
      validatePendingMembers,
      addMembers,

      wizardStep,
      setWizardStep,
      detailsName,
      setDetailsName,
      detailsType,
      setDetailsType,
      detailsChannels,
      setDetailsChannels,
      detailsDescription,
      setDetailsDescription,
      detailsDescriptionError,
      setDetailsDescriptionError,
      detailsError,
      setDetailsError,
      wizardAction,
      setWizardAction,

      importMethod,
      setImportMethod,
      files,
      setFiles,
      csvData,
      setCsvData,
      importHeaderless,
      wizardListId,
      setWizardListId,
      draftId,
      setDraftId,
      detectedHeaders,
      setDetectedHeaders,
      generatedMapping,
      setGeneratedMapping,
      userMapping,
      setUserMapping,
      previewSummary,
      setPreviewSummary,
      commitStatus,
      setCommitStatus,
      importDraftStatus,
      setImportDraftStatus,
      detectImport,
      previewImport,
      commitImport,
      pollImportStatus,
      resetImportWizard,
      resetListForm
    }),
    [
      rows,
      rowCount,
      selectedList,
      members,
      memberTotal,
      pendingMembers,
      wizardStep,
      detailsName,
      detailsType,
      detailsChannels,
      detailsDescription,
      detailsDescriptionError,
      detailsError,
      wizardAction,
      importMethod,
      files,
      csvData,
      importHeaderless,
      wizardListId,
      draftId,
      detectedHeaders,
      generatedMapping,
      userMapping,
      previewSummary,
      commitStatus,
      importDraftStatus,
      fetchTestSeedLists,
      createTestSeedList,
      getTestSeedListById,
      updateTestSeedList,
      deleteTestSeedList,
      bulkDeleteTestSeedLists,
      bulkArchiveTestSeedLists,
      bulkUnarchiveTestSeedLists,
      archiveTestSeedList,
      unarchiveTestSeedList,
      getMembers,
      updateMember,
      deleteMember,
      addPendingMember,
      updatePendingMember,
      removePendingMember,
      validatePendingMembers,
      addMembers,
      detectImport,
      previewImport,
      commitImport,
      pollImportStatus,
      resetImportWizard,
      resetListForm
    ]
  )

  return <TestSeedListContext.Provider value={values}>{children}</TestSeedListContext.Provider>
}

TestSeedListProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export { TestSeedListContext, TestSeedListProvider, defaultProvider }
